"use client";

/**
 * CustomThemeBuilder — color-picker + typography + background based theme creator.
 * Every change live-applies to the running UI automatically (debounced 150ms).
 * Users can also save to Firestore, export as JSON, and import from JSON.
 */

import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Download,
  Loader2,
  RefreshCw,
  Save,
  Upload,
  Type,
  ChevronDown,
  ChevronUp,
  X,
  Image as ImageIcon,
  AlertTriangle,
  Wand2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { useTheme } from "@/contexts/theme-context";
import { applyThemeTokensToCss, applyBackgrounds, hexToHsl, loadFont, fixContrastTokens } from "@/lib/theme-utils";
import { createTheme } from "@/services/theme";
import { plainLightTokens } from "@/lib/theme-presets";
import { useToast } from "@/hooks/use-toast";
import type { ThemeTokens, ColorTokens, TypographyTokens, BackgroundTokens, SectionBackground } from "@/types/theme";
import { FontPicker as SharedFontPicker } from "@/components/ui/font-picker";
import { ensureFontLoaded } from "@/lib/fonts";

// ─── Colour key list ────────────────────────────────────────────────────────
interface ColorKey {
  key: keyof ColorTokens;
  label: string;
  group: string;
}

const COLOR_KEYS: ColorKey[] = [
  { key: "background",              label: "Background",         group: "Page" },
  { key: "foreground",              label: "Text",               group: "Page" },
  { key: "headerBackground",        label: "Header",             group: "Page" },
  { key: "card",                    label: "Cards bg",           group: "Page" },
  { key: "cardForeground",          label: "Cards text",         group: "Page" },
  { key: "popover",                 label: "Popover bg",         group: "Page" },
  { key: "popoverForeground",       label: "Popover text",       group: "Page" },
  { key: "border",                  label: "Borders",            group: "Page" },
  { key: "lines",                   label: "Lines & outlines",   group: "Page" },
  { key: "primary",                 label: "Primary",            group: "Accents" },
  { key: "primaryForeground",       label: "Primary Text",       group: "Accents" },
  { key: "secondary",               label: "Secondary",          group: "Accents" },
  { key: "secondaryForeground",     label: "Secondary Text",     group: "Accents" },
  { key: "accent",                  label: "Accent",             group: "Accents" },
  { key: "accentForeground",        label: "Accent Text",        group: "Accents" },
  { key: "muted",                   label: "Muted",              group: "Accents" },
  { key: "mutedForeground",         label: "Muted Text",         group: "Accents" },
  { key: "destructive",             label: "Destructive",        group: "Accents" },
  { key: "destructiveForeground",   label: "Destructive Text",   group: "Accents" },
  { key: "sidebarBackground",       label: "Sidebar bg",         group: "Sidebar" },
  { key: "sidebarForeground",       label: "Sidebar text",       group: "Sidebar" },
  { key: "sidebarPrimary",          label: "Sidebar active bg",  group: "Sidebar" },
  { key: "sidebarPrimaryForeground",label: "Sidebar active text",group: "Sidebar" },
  { key: "sidebarAccent",           label: "Sidebar accent bg",  group: "Sidebar" },
  { key: "sidebarAccentForeground", label: "Sidebar accent text",group: "Sidebar" },
  { key: "icons",                   label: "Menu icons",         group: "Sidebar" },
  { key: "playerToggleBackground",  label: "Play Button",        group: "Player" },
];

// ─── Background sections ─────────────────────────────────────────────────────
const BACKGROUND_SECTIONS: Array<{ key: keyof BackgroundTokens; label: string }> = [
  { key: "body",       label: "Page Body" },
  { key: "header",     label: "Header Bar" },
  { key: "sidebar",    label: "Side Menu" },
  { key: "card",       label: "Cards" },
  { key: "player",     label: "Media Player" },
  { key: "chatHeader", label: "Chat Header" },
  { key: "chatBody",   label: "Chat Body" },
  { key: "chatButton", label: "Chat Input Bar" },
];

// ─── HSL ↔ hex helpers ─────────────────────────────────────────────────────
function hslStringToHex(hsl: string): string {
  const parts = hsl.trim().split(/\s+/);
  if (parts.length < 3) return "#888888";
  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1]) / 100;
  const l = parseFloat(parts[2]) / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// ─── WCAG contrast utilities ────────────────────────────────────────────────
function hslToLinear(hsl: string): number | null {
  try {
    const parts = hsl.trim().split(/\s+/);
    if (parts.length < 3) return null;
    const h = parseFloat(parts[0]);
    const s = parseFloat(parts[1]) / 100;
    const l = parseFloat(parts[2]) / 100;
    const a = s * Math.min(l, 1 - l);
    // Convert HSL → sRGB channel (0..1) then linearise
    const toLinearChannel = (n: number): number => {
      const k = (n + h / 30) % 12;
      const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      // sRGB linearisation
      return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    const R = toLinearChannel(0);
    const G = toLinearChannel(8);
    const B = toLinearChannel(4);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  } catch {
    return null;
  }
}

/** Returns the WCAG 2.1 contrast ratio between two HSL strings (e.g. "240 10% 4%") */
function wcagContrast(hsl1: string, hsl2: string): number {
  const l1 = hslToLinear(hsl1);
  const l2 = hslToLinear(hsl2);
  if (l1 === null || l2 === null) return 21;
  const lighter = Math.max(l1, l2);
  const darker  = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Pairs of (foreground, background) token keys to validate.
 *  Must stay in sync with CONTRAST_FIX_PAIRS in theme-utils.ts */
const CONTRAST_PAIRS: Array<{ fg: keyof ColorTokens; bg: keyof ColorTokens; label: string }> = [
  { fg: "foreground",               bg: "background",        label: "Page text on background" },
  { fg: "foreground",               bg: "card",              label: "Page text on cards" },
  { fg: "cardForeground",           bg: "card",              label: "Card text on card bg" },
  { fg: "popoverForeground",        bg: "popover",           label: "Popover text on popover" },
  { fg: "primaryForeground",        bg: "primary",           label: "Text on primary buttons" },
  { fg: "secondaryForeground",      bg: "secondary",         label: "Text on secondary elements" },
  { fg: "accentForeground",         bg: "accent",            label: "Text on accent elements" },
  { fg: "mutedForeground",          bg: "muted",             label: "Muted text on muted bg" },
  { fg: "destructiveForeground",    bg: "destructive",       label: "Text on destructive/error" },
  { fg: "sidebarForeground",        bg: "sidebarBackground", label: "Sidebar text on sidebar bg" },
  { fg: "sidebarPrimaryForeground", bg: "sidebarPrimary",    label: "Sidebar active text" },
  { fg: "sidebarAccentForeground",  bg: "sidebarAccent",     label: "Sidebar accent text" },
];

function getContrastLevel(ratio: number): { level: "pass" | "warn" | "fail"; label: string } {
  if (ratio >= 4.5) return { level: "pass",  label: "AA ✓" };
  if (ratio >= 3.0) return { level: "warn",  label: "Low" };
  return                  { level: "fail",  label: "Fail" };
}

// ─── Single colour picker row ──────────────────────────────────────────────
function ColorRow({ label, hslValue, onChange, contrastWith, contrastLabel }: {
  label: string;
  hslValue: string;
  onChange: (hsl: string) => void;
  /** If provided, show a contrast badge against this HSL value */
  contrastWith?: string;
  contrastLabel?: string;
}) {
  const hexValue = hslStringToHex(hslValue);
  const contrast = contrastWith != null ? wcagContrast(hslValue, contrastWith) : null;
  const info = contrast != null ? getContrastLevel(contrast) : null;

  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={hexValue}
        onChange={(e) => { const hsl = hexToHsl(e.target.value); if (hsl) onChange(hsl); }}
        className="h-7 w-7 rounded border border-border cursor-pointer flex-shrink-0 p-0.5"
        style={{ backgroundColor: hexValue }}
        title={label}
      />
      <Label className="text-xs flex-1 truncate text-muted-foreground">{label}</Label>
      {info && info.level !== "pass" && (
        <span
          title={`Contrast ratio ${contrast!.toFixed(1)}:1 vs ${contrastLabel ?? "background"}. WCAG AA requires 4.5:1 for normal text.`}
          className={cn(
            "text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 cursor-help",
            info.level === "fail"
              ? "bg-destructive/15 text-destructive"
              : "bg-amber-500/15 text-amber-600 dark:text-amber-400"
          )}
        >
          {info.level === "fail" ? "⚠ Low contrast" : "⚠ Borderline"}
        </span>
      )}
      <code className="text-[10px] text-muted-foreground/70 hidden sm:block font-mono w-20 text-right truncate">{hslValue}</code>
    </div>
  );
}

// ─── Font family picker ─────────────────────────────────────────────────────
/**
 * Theme font picker — a thin adapter over the shared <FontPicker>, so a theme
 * can use ANY of the ~1,900 Google families or an uploaded font, not just the
 * 51 hardcoded in lib/google-fonts.ts.
 *
 * Themes store a full CSS stack ("'Inter', sans-serif"); the shared picker
 * speaks bare family names, so this converts in both directions.
 */
function FontPicker({ value, onChange }: { value: string; onChange: (cssFamily: string) => void }) {
  const family = value.split(",")[0].replace(/['"]/g, "").trim();
  return (
    <SharedFontPicker
      value={family}
      placeholder="Inter (default)"
      onChange={(f) => {
        if (!f) { onChange("'Inter', sans-serif"); return; }
        ensureFontLoaded(f).catch(() => {});
        onChange(`'${f}', sans-serif`);
      }}
    />
  );
}

// ─── Font size slider row ───────────────────────────────────────────────────
function FontSizeRow({ label, value, onChange }: {
  label: string; value: string; onChange: (v: string) => void;
}) {
  const numVal = parseFloat(value) || 1;
  return (
    <div className="flex items-center gap-2">
      <Label className="text-xs text-muted-foreground w-12 flex-shrink-0">{label}</Label>
      <input
        type="range"
        min={0.625}
        max={4}
        step={0.125}
        value={numVal}
        onChange={e => onChange(`${parseFloat(e.target.value)}rem`)}
        className="flex-1 h-1.5 accent-primary cursor-pointer"
      />
      <span className="text-[10px] font-mono text-muted-foreground w-12 text-right flex-shrink-0">{value}</span>
    </div>
  );
}

// ─── Background section row ─────────────────────────────────────────────────
function BgSectionRow({ label, value, onChange }: {
  label: string;
  value: SectionBackground;
  onChange: (v: SectionBackground) => void;
}) {
  const hasMedia = !!(value.image || value.video);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground font-medium">{label}</Label>
      <div className="space-y-1.5 pl-2 border-l-2 border-border/50">
        {/* Image URL */}
        <div className="flex items-center gap-2">
          <Label className="text-[10px] text-muted-foreground w-10 flex-shrink-0">Image</Label>
          <Input
            value={value.image ?? ""}
            onChange={e => onChange({ ...value, image: e.target.value || undefined })}
            placeholder="https://…"
            className="h-7 text-xs flex-1"
          />
          {value.image && (
            <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0"
              onClick={() => onChange({ ...value, image: undefined })}>
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        {/* Video URL */}
        <div className="flex items-center gap-2">
          <Label className="text-[10px] text-muted-foreground w-10 flex-shrink-0">Video</Label>
          <Input
            value={value.video ?? ""}
            onChange={e => onChange({ ...value, video: e.target.value || undefined })}
            placeholder="https://….mp4"
            className="h-7 text-xs flex-1"
          />
          {value.video && (
            <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0"
              onClick={() => onChange({ ...value, video: undefined })}>
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        {/* Opacity slider — only when media is set */}
        {hasMedia && (
          <div className="flex items-center gap-2">
            <Label className="text-[10px] text-muted-foreground w-10 flex-shrink-0">Opacity</Label>
            <input
              type="range"
              min={0.05}
              max={1}
              step={0.05}
              value={value.opacity ?? 0.15}
              onChange={e => onChange({ ...value, opacity: parseFloat(e.target.value) })}
              className="flex-1 h-1.5 accent-primary cursor-pointer"
            />
            <span className="text-[10px] font-mono text-muted-foreground w-10 text-right flex-shrink-0">
              {Math.round((value.opacity ?? 0.15) * 100)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Mini live preview ─────────────────────────────────────────────────────
// Today's layout (Gordon 2026-11-02: "custom preview icon is the old one"):
// header across the top (search on one side, menu button on the other), the
// menu column on the menu side, the VU strip on the other edge, hero + filter
// chips + square thumbnails, and the player bar along the bottom with the chat
// button in the menu-side corner. Mirrors with the person's menu side.
function MiniPreview({ colors, fontFamily }: { colors: ColorTokens; fontFamily: string }) {
  const [menuRight, setMenuRight] = useState(true);
  useEffect(() => { setMenuRight(document.documentElement.dataset.menuSide !== "left"); }, []);
  const c = (v?: string) => (v ? `hsl(${v})` : undefined);
  const bg = c(colors.background);
  const fg = c(colors.foreground)!;
  const primary = c(colors.primary);
  const sidebar = c(colors.sidebarBackground);
  const card = c(colors.card);
  const header = c((colors as any).headerBackground) ?? bg;
  const line = colors.lines ? c(colors.lines)! : `hsl(${colors.foreground} / 0.4)`;
  const icon = c(colors.icons) ?? `hsl(${colors.sidebarForeground} / 0.8)`;
  const square = (extra?: React.CSSProperties) => <div className="h-4 w-4 shrink-0" style={{ border: `1px solid ${line}`, ...extra }} />;
  const menuCol = (
    <div className="flex w-5 shrink-0 flex-col items-center gap-1.5 py-1.5" style={{ background: sidebar }}>
      {[0, 1, 2, 3, 4].map(i => <div key={i} className="h-2 w-2 rounded-[2px]" style={{ border: `1px solid ${icon}`, background: i === 0 ? c(colors.sidebarPrimary) : undefined }} />)}
    </div>
  );
  const vuCol = (
    <div className="flex w-2 shrink-0 flex-col justify-end gap-[2px] px-[2px] pb-1" style={{ background: sidebar }}>
      {[0.9, 0.7, 0.5, 0.35].map((o, i) => <div key={i} className="h-1 w-full" style={{ background: primary, opacity: o }} />)}
    </div>
  );
  return (
    <div className="w-full overflow-hidden rounded-xl border border-border shadow-md" style={{ background: bg, fontFamily }}>
      {/* Header */}
      <div className={cn("flex items-center gap-1.5 px-1 py-1", menuRight ? "" : "flex-row-reverse")} style={{ background: header }}>
        {square()}
        <div className="h-1.5 flex-1 rounded" style={{ background: fg, opacity: 0.55 }} />
        {square({ background: sidebar })}
      </div>
      <div className={cn("flex", menuRight ? "" : "flex-row-reverse")} style={{ minHeight: "96px" }}>
        {vuCol}
        {/* Page */}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-1.5">
          <div className="relative h-8 overflow-hidden rounded-[3px]" style={{ background: card }}>
            <div className="absolute bottom-1 left-1.5 h-1.5 w-1/2 rounded" style={{ background: fg, opacity: 0.85 }} />
          </div>
          <div className="flex gap-1">
            {[0, 1, 2, 3].map(i => <div key={i} className="h-2.5 w-7 rounded-full" style={i === 0 ? { background: primary } : { border: `1px solid ${line}` }} />)}
          </div>
          <div className="grid grid-cols-5 gap-1">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="flex flex-col gap-0.5">
                <div className="aspect-square rounded-[2px]" style={{ background: i % 2 ? c(colors.muted) : card }} />
                <div className="h-0.5 w-3/4 rounded" style={{ background: fg, opacity: 0.6 }} />
              </div>
            ))}
          </div>
        </div>
        {menuCol}
      </div>
      {/* Player bar + chat button in the menu-side corner */}
      <div className={cn("flex items-center gap-1.5 border-t pl-1", menuRight ? "" : "flex-row-reverse pl-0 pr-1")} style={{ borderColor: c(colors.border), background: header }}>
        <div className="h-4 w-4 shrink-0 rounded-[2px]" style={{ background: c(colors.muted) }} />
        <div className="h-1 w-8 shrink-0 rounded" style={{ background: fg, opacity: 0.7 }} />
        <div className="h-3 w-3 shrink-0 rounded-full" style={{ background: c((colors as any).playerToggleBackground) ?? primary }} />
        <div className="h-1 flex-1 rounded" style={{ background: fg, opacity: 0.35 }} />
        <div className="flex h-5 w-5 shrink-0 items-center justify-center" style={{ background: sidebar }}>
          <div className="h-2 w-2.5 rounded-[2px]" style={{ border: `1px solid ${icon}` }} />
        </div>
      </div>
      <div className="flex items-center gap-1 border-t px-3 py-1.5" style={{ borderColor: c(colors.border) }}>
        {[bg, primary, c(colors.accent), sidebar].map((col, i) => (
          <div key={i} className="h-3 w-3 rounded-full border border-white/20" style={{ background: col }} />
        ))}
        <span className="ml-auto text-[10px]" style={{ color: fg, opacity: 0.6 }}>Aa Bb Cc</span>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export function CustomThemeBuilder() {
  const { user } = useAuth();
  const { currentTheme, setUserTheme, reloadTheme } = useTheme();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [colors, setColors] = useState<ColorTokens>(() => ({
    ...plainLightTokens.colors,
    ...(currentTheme?.tokens?.colors ?? {}),
  }));

  const [typography, setTypography] = useState<TypographyTokens>(() => ({
    ...plainLightTokens.typography,
    ...(currentTheme?.tokens?.typography ?? {}),
  }));

  const [backgrounds, setBackgrounds] = useState<BackgroundTokens>(() =>
    currentTheme?.tokens?.backgrounds ?? {}
  );

  const [customFontUrl, setCustomFontUrl] = useState("");
  const [typographyOpen, setTypographyOpen] = useState(false);
  const [backgroundsOpen, setBackgroundsOpen] = useState(false);
  const [themeName, setThemeName] = useState("My Custom Theme");
  const [saving, setSaving] = useState(false);

  const handleColorChange = useCallback((key: keyof ColorTokens, hsl: string) => {
    setColors(prev => ({ ...prev, [key]: hsl }));
  }, []);

  const handleTypoChange = useCallback(<K extends keyof TypographyTokens>(key: K, val: string) => {
    setTypography(prev => ({ ...prev, [key]: val }));
  }, []);

  const handleBgChange = useCallback((key: keyof BackgroundTokens, val: SectionBackground) => {
    setBackgrounds(prev => ({ ...prev, [key]: val }));
  }, []);

  const buildTokens = (): ThemeTokens => ({
    colors,
    typography,
    spacing: currentTheme?.tokens?.spacing ?? plainLightTokens.spacing,
    radius: currentTheme?.tokens?.radius ?? plainLightTokens.radius,
    shadows: plainLightTokens.shadows,
    backgrounds,
  });

  const injectCustomFontUrl = (url: string) => {
    if (!url.trim() || typeof document === "undefined") return;
    const existing = document.querySelector(`link[data-custom-font]`);
    if (existing) existing.remove();
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url.trim();
    link.setAttribute("data-custom-font", "true");
    document.head.appendChild(link);
  };

  // ── Live-apply every change automatically (debounced 150ms) ──────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customFontUrl) injectCustomFontUrl(customFontUrl);
      loadFont(typography.fontGlobal);
      applyThemeTokensToCss({
        colors,
        typography,
        spacing: currentTheme?.tokens?.spacing ?? plainLightTokens.spacing,
        radius: currentTheme?.tokens?.radius ?? plainLightTokens.radius,
        shadows: plainLightTokens.shadows,
        backgrounds,
      });
      // applyThemeTokensToCss already calls applyBackgrounds, but call explicitly too
      // in case backgrounds changed independently
      applyBackgrounds(backgrounds);
    }, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors, typography, backgrounds, customFontUrl]);

  const handleReset = () => {
    setColors({ ...plainLightTokens.colors, ...(currentTheme?.tokens?.colors ?? {}) });
    setTypography({ ...plainLightTokens.typography, ...(currentTheme?.tokens?.typography ?? {}) });
    setBackgrounds(currentTheme?.tokens?.backgrounds ?? {});
    setCustomFontUrl("");
    reloadTheme();
  };

  const handleSave = async () => {
    if (!user) { toast({ variant: "destructive", title: "Sign in to save themes" }); return; }
    setSaving(true);
    try {
      if (customFontUrl) injectCustomFontUrl(customFontUrl);
      await loadFont(typography.fontGlobal);
      const newTheme = await createTheme({
        name: themeName,
        scope: "user" as const,
        status: "active" as const,
        isDefault: false,
        tokens: buildTokens(),
        customFontUrl: customFontUrl || undefined,
        createdByUid: user.uid,
      } as any);
      if (newTheme && (newTheme as any).id) await setUserTheme((newTheme as any).id);
      toast({ title: `"${themeName}" saved and applied` });
    } catch {
      toast({ variant: "destructive", title: "Failed to save theme" });
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    const payload = { name: themeName, tokens: buildTokens(), customFontUrl: customFontUrl || undefined, version: "1.0" };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${themeName.toLowerCase().replace(/\s+/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Theme exported" });
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string);
        if (data.tokens?.colors) {
          setColors({ ...plainLightTokens.colors, ...data.tokens.colors });
          if (data.tokens.typography) setTypography({ ...plainLightTokens.typography, ...data.tokens.typography });
          if (data.tokens.backgrounds) setBackgrounds(data.tokens.backgrounds);
          if (data.customFontUrl) { setCustomFontUrl(data.customFontUrl); injectCustomFontUrl(data.customFontUrl); }
          if (data.name) setThemeName(data.name);
          applyThemeTokensToCss({ ...plainLightTokens, ...data.tokens });
          toast({ title: "Theme imported — preview applied" });
        } else {
          toast({ variant: "destructive", title: "Invalid theme file" });
        }
      } catch {
        toast({ variant: "destructive", title: "Failed to read file" });
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const groups = ["Page", "Accents", "Sidebar", "Player"];
  const currentFontFamily = typography.fontGlobal || "'Inter', sans-serif";

  // Build a lookup: colorKey → partner key for contrast checking
  const contrastPartnerMap = React.useMemo(() => {
    const map = new Map<keyof ColorTokens, { partnerKey: keyof ColorTokens; label: string }>();
    for (const pair of CONTRAST_PAIRS) {
      map.set(pair.fg, { partnerKey: pair.bg, label: pair.label });
      map.set(pair.bg, { partnerKey: pair.fg, label: pair.label });
    }
    return map;
  }, []);

  // Failing/warning contrast pairs for the summary banner
  const contrastIssues = React.useMemo(() =>
    CONTRAST_PAIRS
      .map(pair => ({
        ...pair,
        ratio: wcagContrast(colors[pair.fg] as string, colors[pair.bg] as string),
      }))
      .filter(p => p.ratio < 4.5)
      .sort((a, b) => a.ratio - b.ratio),
    [colors]
  );

  return (
    <div className="space-y-5">
      {/* Live preview */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Preview</p>
        <MiniPreview colors={colors} fontFamily={currentFontFamily} />
        <p className="text-[10px] text-muted-foreground mt-1.5 text-center">Changes apply to the live UI automatically</p>
      </div>

      {/* Contrast warnings summary */}
      {contrastIssues.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/8 p-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              <p className="text-xs font-semibold">
                {contrastIssues.filter(i => i.ratio < 3).length > 0 ? "Contrast issues — text may be invisible" : "Low contrast warnings"}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-6 text-[10px] px-2 flex-shrink-0 border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
              onClick={() => {
                const fixed = fixContrastTokens(colors);
                setColors(fixed);
                toast({ title: "Contrast fixed", description: "Text colors adjusted to meet WCAG AA (4.5:1)." });
              }}
            >
              <Wand2 className="h-3 w-3 mr-1" />
              Auto-fix
            </Button>
          </div>
          {contrastIssues.map(issue => (
            <div key={`${issue.fg}-${issue.bg}`} className="flex items-center justify-between gap-2 text-[10px]">
              <span className="text-muted-foreground truncate">{issue.label}</span>
              <span className={cn(
                "font-mono font-bold flex-shrink-0",
                issue.ratio < 3 ? "text-destructive" : "text-amber-600 dark:text-amber-400"
              )}>
                {issue.ratio.toFixed(1)}:1
                {issue.ratio < 3 ? " ✗" : " ~"}
              </span>
            </div>
          ))}
          <p className="text-[10px] text-muted-foreground/70 pt-0.5">WCAG AA requires 4.5:1 for normal text.</p>
        </div>
      )}

      {/* Theme name */}
      <div className="space-y-1">
        <Label className="text-xs">Theme Name</Label>
        <Input value={themeName} onChange={e => setThemeName(e.target.value)} className="h-8 text-sm" placeholder="My Custom Theme" />
      </div>

      {/* Colour pickers */}
      {groups.map(group => {
        const keys = COLOR_KEYS.filter(k => k.group === group);
        return (
          <div key={group} className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{group}</p>
            <div className="space-y-2 pl-1">
              {keys.map(({ key, label }) => {
                const partner = contrastPartnerMap.get(key);
                const partnerHsl = partner ? (colors[partner.partnerKey] as string | undefined) : undefined;
                return (
                  <ColorRow
                    key={key}
                    label={label}
                    // Lines / Menu icons start from what they follow today (Text / Sidebar text).
                    hslValue={(colors[key] ?? (key === "lines" ? colors.foreground : key === "icons" ? colors.sidebarForeground : "")) as string}
                    onChange={hsl => handleColorChange(key, hsl)}
                    contrastWith={partnerHsl}
                    contrastLabel={partner?.label}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      <Separator />

      {/* ── Typography ──────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <button
          type="button"
          className="flex w-full items-center justify-between"
          onClick={() => setTypographyOpen(o => !o)}
        >
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Type className="h-3 w-3" />
            Typography
          </p>
          {typographyOpen
            ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
            : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </button>

        {typographyOpen && (
          <div className="space-y-4 pl-1 pt-1">

            {/* Google Font picker */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Font Family — all Google Fonts + uploaded fonts</Label>
              <FontPicker
                value={currentFontFamily}
                onChange={family => {
                  handleTypoChange("fontGlobal", family);
                  loadFont(family);
                }}
              />
            </div>

            {/* Custom web font URL */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Custom Web Font URL</Label>
              <div className="flex gap-2">
                <Input
                  value={customFontUrl}
                  onChange={e => setCustomFontUrl(e.target.value)}
                  placeholder="https://fonts.googleapis.com/css2?family=…"
                  className="h-8 text-xs flex-1"
                />
                {customFontUrl && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => setCustomFontUrl("")} title="Clear custom URL">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                Paste any Google Fonts, Bunny Fonts, or web-font stylesheet URL.
                For DaFont fonts: download → convert to .woff2 → upload to Firebase Storage → create a
                {" "}<code className="bg-muted px-0.5 rounded">@font-face</code> CSS stylesheet → paste the stylesheet URL here.
              </p>
              {customFontUrl && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">CSS font-family value to apply</Label>
                  <Input
                    value={typography.fontGlobal}
                    onChange={e => handleTypoChange("fontGlobal", e.target.value)}
                    placeholder="'MyFont', sans-serif"
                    className="h-8 text-xs font-mono"
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* Font sizes */}
            <div className="space-y-2.5">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">Font Sizes</Label>
              <FontSizeRow label="H1" value={typography.fontSizeH1} onChange={v => handleTypoChange("fontSizeH1", v)} />
              <FontSizeRow label="H2" value={typography.fontSizeH2} onChange={v => handleTypoChange("fontSizeH2", v)} />
              <FontSizeRow label="H3" value={typography.fontSizeH3} onChange={v => handleTypoChange("fontSizeH3", v)} />
              <FontSizeRow label="Body" value={typography.fontSizeP} onChange={v => handleTypoChange("fontSizeP", v)} />
            </div>

            {/* Live font preview */}
            <div className="rounded-md border border-border p-3 bg-muted/30 space-y-0.5" style={{ fontFamily: currentFontFamily }}>
              <p style={{ fontSize: typography.fontSizeH1, lineHeight: 1.2, fontWeight: 700 }} className="truncate">Heading 1</p>
              <p style={{ fontSize: typography.fontSizeH2, lineHeight: 1.3, fontWeight: 600 }} className="truncate">Heading 2</p>
              <p style={{ fontSize: typography.fontSizeH3, lineHeight: 1.4, fontWeight: 500 }} className="truncate">Heading 3</p>
              <p style={{ fontSize: typography.fontSizeP, lineHeight: 1.6 }} className="text-muted-foreground">
                Body — The quick brown fox jumps over the lazy dog.
              </p>
            </div>

            <p className="text-[10px] text-muted-foreground">
              Font colors are set by the <strong>Text</strong>, <strong>Muted Text</strong>, and <strong>Primary Text</strong> color pickers above.
            </p>
          </div>
        )}
      </div>

      <Separator />

      {/* ── Backgrounds ─────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <button
          type="button"
          className="flex w-full items-center justify-between"
          onClick={() => setBackgroundsOpen(o => !o)}
        >
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <ImageIcon className="h-3 w-3" />
            Backgrounds
          </p>
          {backgroundsOpen
            ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
            : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </button>

        {backgroundsOpen && (
          <div className="space-y-4 pl-1 pt-1">
            {BACKGROUND_SECTIONS.map(({ key, label }) => (
              <BgSectionRow
                key={key}
                label={label}
                value={backgrounds[key] ?? {}}
                onChange={val => handleBgChange(key, val)}
              />
            ))}
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Paste any image or video URL for each section. Opacity controls how strongly the media
              shows through the existing background color. Changes apply to the live UI instantly.
            </p>
          </div>
        )}
      </div>

      <Separator />

      {/* Actions */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="flex-1 h-8 text-xs" onClick={handleReset} title="Reset to active theme">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Reset
          </Button>
        </div>

        <Button size="sm" className="w-full h-8 text-xs" onClick={handleSave} disabled={saving || !themeName.trim()}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          Save &amp; Apply
        </Button>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={handleExport}>
            <Download className="h-3.5 w-3.5 mr-1.5" />Export JSON
          </Button>
          <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-3.5 w-3.5 mr-1.5" />Import JSON
          </Button>
          <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleImport} />
        </div>
      </div>

      <p className="text-[10px] text-muted-foreground text-center">
        Saved themes appear in your profile settings and can be shared via JSON export.
      </p>
    </div>
  );
}
